import { BadgePercent, Building2, Clock3, Coins, Download, Plus, Scale, Search, Wallet } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { confirmModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { fmtMoney } from '@/features/shared/format'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { HR_PAYROLL_PARAMS_PERM, HR_PAYROLL_WRITE_PERM } from '../permissions'
import { useClosePayrollPeriod, usePayrollPeriod, usePersonelList } from './api'
import { BordroSummary } from './components/bordro/BordroSummary'
import { BordroSummaryModal } from './components/bordro/BordroSummaryModal'
import { BordroTable } from './components/bordro/BordroTable'
import { IncentiveCatalogModal } from './components/bordro/IncentiveCatalogModal'
import { PayrollParamsModal } from './components/bordro/PayrollParamsModal'
import { HrSurface } from './components/HrSurface'
import { KpiCard } from './components/KpiCard'
import { SectionCard } from './components/SectionCard'
import { exportBordroCsv } from './lib/bordroActions'
import type { BordroDurum } from './types'
import { apiErrorMessage } from '@/i18n'

function periodOptions() {
  const now = new Date()
  return Array.from({ length: 6 }, (_, i) => {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    const raw = date.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })
    return { value, label: raw.charAt(0).toLocaleUpperCase('tr') + raw.slice(1) }
  })
}

const STEPS = ['Bordro verilerini hazırla', 'Ek kazanç ve kesintileri kontrol et', 'Personel bazlı ön kontrol', 'Bordroları onayla', 'Ödemeleri oluştur', 'Bordro dökümlerini yayınla']

export function BordroPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const donemler = useMemo(() => periodOptions(), [])
  const people = usePersonelList()
  const [q, setQ] = useState('')
  const [durum, setDurum] = useState<BordroDurum | 'all'>('all')
  const [donem, setDonem] = useState(donemler[0]?.value ?? '')
  const payroll = usePayrollPeriod(donem)
  const close = useClosePayrollPeriod()
  const all = payroll.data?.rows ?? []
  const notGenerated = payroll.data ? !payroll.data.generated : false
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [summaryId, setSummaryId] = useState<string | null>(null)
  const [paramsOpen, setParamsOpen] = useState(false)
  const [incentivesOpen, setIncentivesOpen] = useState(false)
  const donemLabel = donemler.find((d) => d.value === donem)?.label ?? donem
  const activeId = selectedId && all.some((row) => row.id === selectedId) ? selectedId : (all[0]?.id ?? null)

  const rows = useMemo(() => {
    const n = q.trim().toLocaleLowerCase('tr')
    return all.filter(
      (r) =>
        (durum === 'all' || r.durum === durum) &&
        (!n || r.ad.toLocaleLowerCase('tr').includes(n) || r.sicil.includes(n) || r.departman.toLocaleLowerCase('tr').includes(n)),
    )
  }, [all, q, durum])

  const selected = all.find((r) => r.id === activeId) ?? null
  const summaryRow = all.find((r) => r.id === summaryId) ?? null
  const bekleyen = all.filter((r) => r.durum !== 'onay').length
  const sum = (pick: (row: (typeof all)[number]) => string) => all.reduce((n, row) => n + Number(pick(row) || 0), 0)
  const steps = STEPS.map((label, index) => {
    const state: 'done' | 'act' | 'todo' =
      index === 0 ? (all.length ? 'done' : 'act') : index === 3 && all.length > 0 && bekleyen === 0 ? 'done' : index === 2 && bekleyen > 0 ? 'act' : 'todo'
    return { label, state }
  })

  const openSummary = (id: string) => {
    setSelectedId(id)
    setSummaryId(id)
  }

  const handleCreate = async () => {
    const missing = people.filter((person) => !all.some((row) => row.personel_id === person.id))
    if (missing.length === 0) {
      toast.info(t('hr.personel.actions.bordro.noneMissing'))
      return
    }
    const ok = await confirmModal({
      title: t('confirm.bordroCreate.title'),
      description: t('confirm.bordroCreate.body', { n: missing.length }),
      confirmLabel: t('hr.personel.bordro.olustur'),
    })
    if (!ok) return
    close.mutate(donem, {
      // Hesaplama arka planda yapılır; bitince gerçek zamanlı olayla tablo yenilenir.
      onSuccess: () => toast.success(t('hr.personel.actions.bordro.started', { n: missing.length })),
      onError: (error) => toast.error(apiErrorMessage(error)),
    })
  }

  const showKontrol = () => {
    setDurum('kontrol')
    toast.info(t('hr.personel.actions.bordro.filtered'))
  }

  return (
    <HrSurface>
      <PageHeader
        title={t('hr.personel.bordro.title')}
        description={t('hr.personel.bordro.subtitle')}
        actions={
          <>
            <Select size="lg" className="w-auto min-w-[9.5rem] rounded-xl" value={donem} onChange={setDonem} options={donemler} />
            <Button variant="outline" size="sm" className="bg-card shadow-xs" onClick={() => setParamsOpen(true)}>
              <Scale />
              {t('hr.personel.bordro.params.open')}
            </Button>
            <Button variant="outline" size="sm" className="bg-card shadow-xs" onClick={() => setIncentivesOpen(true)}>
              <BadgePercent />
              {t('hr.personel.tesvik.open')}
            </Button>
            <Button variant="outline" size="sm" className="bg-card shadow-xs" onClick={() => exportBordroCsv(rows, donemLabel)}>
              <Download />
              {t('hr.personel.bordro.excel')}
            </Button>
            {can(HR_PAYROLL_WRITE_PERM) ? (
              <Button size="sm" className="shadow-sm" disabled={close.isPending} onClick={() => void handleCreate()}>
                <Plus />
                {t('hr.personel.bordro.olustur')}
              </Button>
            ) : null}
          </>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 sm:gap-4 xl:grid-cols-4">
        <KpiCard label={t('hr.personel.bordro.kpi.brut')} value={fmtMoney(sum((row) => row.brut).toFixed(2))} tint="success" icon={<Wallet className="size-5" />} />
        <KpiCard label={t('hr.personel.bordro.kpi.net')} value={fmtMoney(sum((row) => row.net).toFixed(2))} tint="info" icon={<Coins className="size-5" />} />
        <KpiCard label={t('hr.personel.bordro.kpi.isveren')} value={fmtMoney(sum((row) => String(Number(row.dokum?.isverenMaliyet ?? row.brut) + Number(row.ek))).toFixed(2))} tint="primary" icon={<Building2 className="size-5" />} />
        <KpiCard label={t('hr.personel.bordro.kpi.bekleyen')} value={bekleyen} tint="warning" icon={<Clock3 className="size-5" />} />
      </div>

      {bekleyen > 0 ? (
      <Card className="rounded-2xl border-info/25 bg-info/8 shadow-sm">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4 text-sm">
          <div>
            <div className="font-semibold text-info">{t('hr.personel.bordro.bannerTitle', { donem: donemLabel, n: bekleyen })}</div>
            <div className="text-muted-foreground">{t('hr.personel.bordro.bannerBody')}</div>
          </div>
          <Button size="sm" variant="outline" className="border-info/30 bg-card text-info" onClick={showKontrol}>
            {t('hr.personel.bordro.bannerCta')}
          </Button>
        </CardContent>
      </Card>
      ) : null}

      <div className="grid grid-cols-[minmax(0,1fr)] gap-5 xl:grid-cols-[minmax(0,1fr)_300px]">
        <SectionCard
          title={t('hr.personel.bordro.tableTitle', { n: all.length })}
          action={
            <div className="flex flex-wrap gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input className="h-10 w-48 rounded-xl bg-muted/40 pl-9 shadow-none" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('hr.personel.bordro.search')} />
              </div>
              <Select
                size="lg"
                className="w-auto min-w-[9.5rem] rounded-xl"
                value={durum}
                onChange={(v) => setDurum(v as BordroDurum | 'all')}
                options={[
                  { value: 'all', label: t('hr.personel.filters.allDurum') },
                  ...(['taslak', 'kontrol', 'onay'] as const).map((d) => ({ value: d, label: t(`hr.personel.bordro.durum.${d}`) })),
                ]}
              />
            </div>
          }
          noPad
        >
          <BordroTable rows={rows} donem={donemLabel} selectedId={activeId} onSelect={setSelectedId} onSummary={openSummary} />
          <div className="px-5 pt-3 text-xs text-muted-foreground max-md:border-t">
            {notGenerated ? t('hr.personel.bordro.notGenerated') : t('hr.personel.pagination', { total: rows.length, from: rows.length ? 1 : 0, to: rows.length })}
          </div>
        </SectionCard>

        <div className="space-y-4">
          <SectionCard title={t('hr.personel.bordro.surec')}>
            <ol className="space-y-3">
              {steps.map((a, i) => (
                <li key={a.label} className="flex items-start gap-3 text-sm">
                  <span
                    className={cn(
                      'flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                      a.state === 'done' && 'bg-success text-white',
                      a.state === 'act' && 'bg-primary text-primary-foreground',
                      a.state === 'todo' && 'bg-muted text-muted-foreground',
                    )}
                  >
                    {i + 1}
                  </span>
                  <span className={cn(a.state === 'todo' && 'text-muted-foreground', a.state === 'act' && 'font-medium')}>{a.label}</span>
                </li>
              ))}
            </ol>
          </SectionCard>

          <BordroSummary row={selected} donem={donemLabel} />
        </div>
      </div>

      <IncentiveCatalogModal open={incentivesOpen} canManage={can(HR_PAYROLL_PARAMS_PERM)} onClose={() => setIncentivesOpen(false)} />
      <PayrollParamsModal open={paramsOpen} canManage={can(HR_PAYROLL_PARAMS_PERM)} onClose={() => setParamsOpen(false)} />
      {summaryRow ? <BordroSummaryModal row={summaryRow} donem={donemLabel} onClose={() => setSummaryId(null)} /> : null}
    </HrSurface>
  )
}
